import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { Api } from '../../api/api';
import { createOptimizerRun, getSchedule } from '../../api/functions';
import { OptimizerRunRequest } from '../../api/models/optimizer-run-request';
import { OptimizerRunResponse } from '../../api/models/optimizer-run-response';
import { ApiProblem } from '../../api/models/api-problem';
import { ScheduleEntity } from '../../api/models/schedule-entity';
import { LocationStateService } from '../../common/services/location-state.service';
import { environment } from '../../environments/environment';

type RunPhase = 'idle' | 'running' | 'success' | 'error';

interface RunLogEntry {
  id: number;
  locationName: string;
  startedAt: string;
  durationMs: number;
  outcome: 'success' | 'error';
  detail: string;
}

interface HorizonInfo {
  start: Date;
  end: Date;
  hours: number;
}

/** Meldungen während des (synchronen) Laufs, nach verstrichenen Sekunden. */
const RUN_STAGES: ReadonlyArray<{ afterSeconds: number; label: string }> = [
  { afterSeconds: 0, label: 'Planungshorizont wird an den Optimierer übergeben…' },
  { afterSeconds: 6, label: 'Wetterprognose, Lastverlauf und Energiepreise werden berechnet…' },
  { afterSeconds: 13, label: 'Optimierungsmodell läuft…' },
];

const DEFAULT_HORIZON_HOURS = 48;
const QUARTER_HOUR_MS = 15 * 60 * 1000;

@Component({
  selector: 'app-optimizer',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatOptionModule,
    MatSelectModule,
  ],
  templateUrl: './optimizer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./optimizer.component.css'],
})
export class OptimizerComponent {
  private readonly api = inject(Api);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly locationState = inject(LocationStateService);

  readonly runPath = createOptimizerRun.PATH;
  readonly horizonPresets = [12, 24, 36, 48, 72] as const;

  readonly selectedLocationId = computed(() => this.locationState.currentLocation()?.id ?? '');
  readonly selectedLocationName = computed(() => this.locationState.currentLocation()?.name ?? '');

  readonly horizonHours = signal<number>(DEFAULT_HORIZON_HOURS);
  readonly requestJson = signal<string>('');

  readonly phase = signal<RunPhase>('idle');
  readonly elapsedMs = signal<number>(0);
  readonly lastDurationMs = signal<number>(0);
  readonly response = signal<OptimizerRunResponse | null>(null);
  readonly errorMessage = signal<string | null>(null);
  readonly runLog = signal<RunLogEntry[]>([]);
  /** Fahrplan, der nach einem erfolgreich gespeicherten Lauf geladen wurde. */
  readonly schedule = signal<ScheduleEntity | null>(null);
  readonly scheduleError = signal<string | null>(null);

  private timerHandle: ReturnType<typeof setInterval> | null = null;
  private runCounter = 0;
  /** Letztes automatisch erzeugtes Anfrage-JSON — damit erkennen wir unveraenderte Textfelder. */
  private generatedRequestJson = '';

  /** Horizont wie im Textfeld eingetragen — das Textfeld hat Vorrang vor den Presets. */
  readonly horizon = computed<HorizonInfo | null>(() => {
    let parsed: Partial<OptimizerRunRequest>;
    try {
      parsed = JSON.parse(this.requestJson()) as Partial<OptimizerRunRequest>;
    } catch {
      return null;
    }
    if (!parsed?.startDate || !parsed?.endDate) {
      return null;
    }
    const start = new Date(parsed.startDate);
    const end = new Date(parsed.endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return null;
    }
    return { start, end, hours: (end.getTime() - start.getTime()) / 3_600_000 };
  });

  readonly horizonIsValid = computed(() => {
    const horizon = this.horizon();
    return !!horizon && horizon.hours > 0;
  });

  readonly isRunning = computed(() => this.phase() === 'running');

  readonly canRun = computed(() => !this.isRunning() && !!this.selectedLocationId() && this.horizonIsValid());

  readonly elapsedLabel = computed(() => this.formatDuration(this.elapsedMs()));
  readonly lastDurationLabel = computed(() => this.formatDuration(this.lastDurationMs()));

  readonly stageLabel = computed(() => {
    const seconds = this.elapsedMs() / 1000;
    let label = RUN_STAGES[0].label;
    for (const stage of RUN_STAGES) {
      if (seconds >= stage.afterSeconds) {
        label = stage.label;
      }
    }
    return label;
  });

  /** Ab hier weisen wir ausdrücklich darauf hin, dass ein langer Lauf in Ordnung ist. */
  readonly isLongRun = computed(() => this.elapsedMs() >= 45_000);

  readonly resultJson = computed(() => {
    const response = this.response();
    return response ? JSON.stringify(response, null, 2) : '';
  });

  constructor() {
    this.setGeneratedRequestJson(DEFAULT_HORIZON_HOURS);
    this.destroyRef.onDestroy(() => this.stopTimer());
  }

  setLocationId(locationId: string): void {
    const next = this.locationState.locations().find((location) => location.id === locationId);
    if (next) {
      this.locationState.setLocation(next);
    }
  }

  /** Setzt den Horizont mit der angegebenen Länge neu auf "jetzt". */
  applyHorizon(hours: number): void {
    this.horizonHours.set(hours);
    this.setGeneratedRequestJson(hours);
  }

  resetToNow(): void {
    this.applyHorizon(this.horizonHours());
  }

  async runOptimizer(): Promise<void> {
    const locationId = this.selectedLocationId();
    if (!locationId) {
      this.errorMessage.set('Bitte zuerst einen Standort wählen.');
      this.phase.set('error');
      return;
    }

    // Ein unveraendertes Textfeld kann inzwischen veraltet sein; dann frisch auf die naechste
    // Viertelstunde setzen, damit startDate nicht in der Vergangenheit liegt.
    if (this.requestJson() === this.generatedRequestJson) {
      this.setGeneratedRequestJson(this.horizonHours());
    }

    let body: OptimizerRunRequest;
    try {
      body = this.parseRequest();
    } catch (error) {
      this.errorMessage.set(this.formatError(error));
      this.phase.set('error');
      return;
    }

    const locationName = this.selectedLocationName() || locationId;
    const startedAt = new Date();
    const startedMs = Date.now();

    this.phase.set('running');
    this.errorMessage.set(null);
    this.response.set(null);
    this.schedule.set(null);
    this.scheduleError.set(null);
    this.startTimer(startedMs);

    try {
      const response = await this.api.invoke(createOptimizerRun, { location_id: locationId, body });
      const durationMs = Date.now() - startedMs;
      this.response.set(response ?? {});
      this.lastDurationMs.set(durationMs);
      this.phase.set('success');
      this.appendLog({
        locationName,
        startedAt,
        durationMs,
        outcome: 'success',
        detail: this.describeOutcome(response ?? {}),
      });
      // Der Lauf ist synchron: ist das Ergebnis gespeichert, liegt der neue Fahrplan bereits vor.
      // Ob er geladen wird, steuert environment.loadGeneratedSchedule.
      if (environment.loadGeneratedSchedule && response?.posted) {
        await this.loadSchedule(locationId);
      }
    } catch (error) {
      const durationMs = Date.now() - startedMs;
      const message = this.formatError(error);
      this.lastDurationMs.set(durationMs);
      this.errorMessage.set(message);
      this.phase.set('error');
      this.appendLog({ locationName, startedAt, durationMs, outcome: 'error', detail: message });
    } finally {
      this.stopTimer();
    }
  }

  dismissResult(): void {
    this.phase.set('idle');
    this.response.set(null);
    this.errorMessage.set(null);
    this.schedule.set(null);
    this.scheduleError.set(null);
  }

  clearLog(): void {
    this.runLog.set([]);
  }

  async copyResult(): Promise<void> {
    const json = this.resultJson();
    if (!json) {
      return;
    }
    try {
      await navigator.clipboard.writeText(json);
    } catch {
      // Zugriff auf die Zwischenablage kann blockiert sein; das Roh-JSON steht ohnehin am Bildschirm.
    }
  }

  formatDuration(ms: number): string {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return minutes > 0 ? `${minutes}m ${String(seconds).padStart(2, '0')}s` : `${seconds}s`;
  }

  private async loadSchedule(locationId: string): Promise<void> {
    try {
      this.schedule.set(await this.api.invoke(getSchedule, { location_id: locationId }));
    } catch (error) {
      this.scheduleError.set(this.formatError(error));
    }
  }

  private parseRequest(): OptimizerRunRequest {
    const horizon = this.horizon();
    if (!horizon) {
      throw new Error('Das Anfrage-JSON muss gültiges JSON mit `startDate` und `endDate` sein.');
    }
    if (horizon.hours <= 0) {
      throw new Error('`endDate` muss nach `startDate` liegen.');
    }
    return {
      startDate: horizon.start.toISOString(),
      endDate: horizon.end.toISOString(),
    };
  }

  private setGeneratedRequestJson(hours: number): void {
    this.generatedRequestJson = this.buildRequestJson(hours);
    this.requestJson.set(this.generatedRequestJson);
  }

  private buildRequestJson(hours: number): string {
    const start = this.ceilToNextQuarterHour(new Date());
    const end = new Date(start.getTime() + hours * 3_600_000);
    const body: OptimizerRunRequest = {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    };
    return JSON.stringify(body, null, 2);
  }

  /**
   * Rundet auf die naechste Viertelstundengrenze in UTC auf (Sekunden und Millisekunden auf null).
   * Liegt der Zeitpunkt genau auf einer Grenze, wird die darauffolgende Grenze genommen.
   * Der Startzeitpunkt muss in der Zukunft liegen, sonst nimmt der Optimizer den Backfill-Pfad
   * und fragt ein Sekunden-Fenster ohne Messwerte ab (RESI 404 -> Optimizer 500).
   */
  private ceilToNextQuarterHour(reference: Date): Date {
    return new Date((Math.floor(reference.getTime() / QUARTER_HOUR_MS) + 1) * QUARTER_HOUR_MS);
  }

  private startTimer(startedMs: number): void {
    this.stopTimer();
    this.elapsedMs.set(0);
    this.timerHandle = setInterval(() => this.elapsedMs.set(Date.now() - startedMs), 250);
  }

  private stopTimer(): void {
    if (this.timerHandle !== null) {
      clearInterval(this.timerHandle);
      this.timerHandle = null;
    }
  }

  private appendLog(entry: Omit<RunLogEntry, 'id' | 'startedAt'> & { startedAt: Date }): void {
    const logEntry: RunLogEntry = {
      id: ++this.runCounter,
      locationName: entry.locationName,
      startedAt: entry.startedAt.toISOString(),
      durationMs: entry.durationMs,
      outcome: entry.outcome,
      detail: entry.detail,
    };
    this.runLog.update((log) => [logEntry, ...log].slice(0, 10));
  }

  private describeOutcome(response: OptimizerRunResponse): string {
    const parts: string[] = [];
    if (response.status) {
      parts.push(response.status);
    }
    if (response.terminationStatus) {
      parts.push(response.terminationStatus);
    }
    parts.push(response.posted ? 'Ergebnis gespeichert' : 'Ergebnis nicht zurückgeschrieben');
    return parts.join(' · ');
  }

  private asRecord(value: unknown): Record<string, unknown> | undefined {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : undefined;
  }

  private formatError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const problem = this.asRecord(error.error) as ApiProblem | undefined;
      const detail = [problem?.title, problem?.detail].filter(Boolean).join(' — ');
      if (error.status === 0) {
        return 'Die API ist nicht erreichbar. Bitte Verbindung prüfen und erneut versuchen.';
      }
      if (error.status === 504) {
        // Deckt "nicht erreichbar" und "Zeitüberschreitung" ab — das Problem-Detail sagt, was zutrifft.
        return `Die API konnte den Optimierer nicht erreichen (504). ${
          detail ||
          'Der Optimierer-Dienst ist möglicherweise nicht verfügbar, oder der Horizont ist zu lang, um ihn rechtzeitig zu lösen.'
        }`;
      }
      if (error.status === 502) {
        return `Der Optimierer konnte die Optimierung nicht ausführen (502). ${detail}`.trim();
      }
      if (error.status === 422) {
        return `Der Optimierer hat die Laufparameter abgelehnt (422). ${detail}`.trim();
      }
      return detail ? `${error.status}: ${detail}` : `${error.status} ${error.statusText}`;
    }
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === 'string') {
      return error;
    }
    try {
      return JSON.stringify(error);
    } catch {
      return 'Unbekannter Fehler.';
    }
  }
}
